import React, { useEffect, useRef, useState } from "react";
import { useLayout } from "../../../Layouts/LayoutContext";
import api from "../../../../api/axios";
import DeviceSectionTree from "./DeviceSectionTree";
import './DeviceSectionTree.css'
import DevicePickerModal from "./DevicePickerModal";
import useDevicesExcel from "./useDevicesExcel";
import "../../../Layouts/Layout.css";

const IconImport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M4 21h16" />
  </svg>
);
const IconExport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 15V3" /><path d="m7 8 5-5 5 5" /><path d="M4 21h16" />
  </svg>
);
const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);

export default function DevicesPanel() {
  const L = useLayout();
  const fileInputRef = useRef(null);
  const [showPicker, setShowPicker] = useState(false);
  const [enclosure, setEnclosure] = useState(null);

  const enclosureRefId = L.selectedProject?.enclosurePlacement?.referenceId;
  const enclosurePartNumber = enclosure?.articleNumber ?? enclosure?.partNumber ?? "";

  useEffect(() => {
    if (!enclosureRefId) {
      setEnclosure(null);
      return;
    }
    let cancelled = false;
    api.get(`/enclosure/${enclosureRefId}`)
      .then((res) => { if (!cancelled) setEnclosure(res.data); })
      .catch(() => { if (!cancelled) setEnclosure(null); });
    return () => { cancelled = true; };
  }, [enclosureRefId]);

  const { importFile, exportFile } = useDevicesExcel({
    project: L.selectedProject,
    devices: L.devices,
    enclosure,
    refreshProject: L.refreshProject,
  });

  const handleImportClick = () => fileInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await importFile(file);
  };

  const handleAddDevice = () => setShowPicker(true);

  //
  // ─────────────────────────────────────────────────────────────
  //  DEVICES OPERATIONS
  // ─────────────────────────────────────────────────────────────
  //

  const handleAddDevicesFromModal = async (selectedRows) => {
    const projectId = L.selectedProject?.id;
    if (!projectId) return;

    const updatedList = [...(L.devices ?? [])];

    for (const r of selectedRows) {
      const sg = r.subGroup;
      const existing = updatedList.find((d) => d.subGroup?.id === sg.id);

      if (existing) {
        const updated = {
          ...existing,
          quantity: r.quantity,
          placeholderQuantity: r.placeholderQuantity,
          articleNumber: sg.articleNumber ?? "",
          deviceType: sg.name ?? "",
          subGroup: sg,
        };
        await api.put(`/projects/${projectId}/enclosure/devices/${existing.id}`, updated);
      } else {
        await api.post(`/projects/${projectId}/enclosure/devices`, {
          quantity: r.quantity,
          placeholderQuantity: r.placeholderQuantity,
          deviceType: sg.name ?? "",
          articleNumber: sg.articleNumber ?? "",
          subGroup: sg,
          enclosureId: L.selectedProject?.enclosurePlacement?.id,
        });
      }
    }

    await L.refreshProject();
  };

  return (
    <div className="pl-panel">
      <header className="pl-devices-head">
        <h2>Devices</h2>
        <div className="pl-icon-row">
          <button className="pl-icon-btn" title="Import devices" onClick={handleImportClick}><IconImport /></button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleImportFile} />
          <button className="pl-icon-btn" title="Export devices" onClick={exportFile}><IconExport /></button>
          <button className="pl-icon-btn" title="Add device" onClick={handleAddDevice}><IconPlus /></button>
        </div>
      </header>

      <div className="pl-body pl-body-devices">
        {L.hasEnclosure ? (
          <DeviceSectionTree
            enclosurePartNumber={enclosurePartNumber}
            enclosureConfiguratorUrl={enclosure?.configuratorUrl ?? L.enclosureConfiguratorUrl}
            sectionPlacements={L.sectionPlacements}
            sections={L.selectedScenario?.sections}
            devices={L.devices}
            onQuantityChange={L.handleQuantityChange}
            onPlaceholderQuantityChange={L.handlePlaceholderQuantityChange}
            onDeleteDevice={L.handleDeleteDevice}
            // Canvas-agnostic now — same ids work whether 2D or 3D (or
            // neither) is currently mounted in the viewer.
            hiddenIds={L.hiddenIds}
            selectedId={L.selectedId}
            onSelect={L.selectId}
            onToggleVisibility={L.toggleHidden}
          />
        ) : (
          <p className="pl-hint">No enclosure selected yet.</p>
        )}
      </div>

      <DevicePickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onAddDevices={handleAddDevicesFromModal}
        existingDevices={L.devices}
      />
    </div>
  );
}